/**
 * Import VELKÝCH GTFS feedů (např. celé PID ~70 MB zip, stop_times ~stovky MB).
 *
 * Trik: stop_times.txt se nikdy nedekomprimuje celý do paměti —
 *  1. unzipSync s filtrem vybalí jen malé soubory (stops, routes, trips, calendar…),
 *  2. zastávky se ořežou na bounding box kolem trasy uživatele,
 *  3. stop_times.txt se STREAMUJE (fflate Unzip) po řádcích a nechávají se
 *     jen řádky zastávek v boxu,
 *  4. z malých souborů + ořezaného stop_times se postaví mini-zip (bez komprese)
 *     a předá existujícímu parseru parseGtfsZip — žádná duplicitní logika.
 *
 * Paměť: O(zip + malé soubory + ořezané řádky); čas O(bajty feedu).
 */
import { strFromU8, strToU8, unzipSync, zipSync, Unzip, UnzipInflate } from 'fflate';
import type { LatLng } from '../core/types';
import { parseGtfsZip, type GtfsImportResult } from './gtfs';

export interface GtfsBbox {
  south: number;
  west: number;
  north: number;
  east: number;
}

/** Bounding box kolem bodů s paddingem v km (hrubý převod na stupně). */
export function bboxAround(points: LatLng[], padKm: number): GtfsBbox {
  const lats = points.map((p) => p.lat);
  const lngs = points.map((p) => p.lng);
  const midLat = (Math.min(...lats) + Math.max(...lats)) / 2;
  const dLat = padKm / 110.574;
  const dLng = padKm / (111.32 * Math.max(0.2, Math.cos((midLat * Math.PI) / 180)));
  return {
    south: Math.min(...lats) - dLat,
    north: Math.max(...lats) + dLat,
    west: Math.min(...lngs) - dLng,
    east: Math.max(...lngs) + dLng,
  };
}

const SMALL_FILES = /(^|\/)(stops|routes|trips|calendar|calendar_dates|feed_info|agency)\.txt$/;

/**
 * Naparsuje GTFS zip s ořezem na bounding box. Vhodné pro velké feedy;
 * malé zvládne taky (jen je ořeže). Vrací výsledek stejně jako parseGtfsZip,
 * s warningem o ořezu.
 */
export async function parseGtfsZipFiltered(
  bytes: Uint8Array,
  opts: { bbox: GtfsBbox; serviceDate?: string; maxLines?: number },
): Promise<GtfsImportResult> {
  // 1) Malé soubory (bez stop_times) — synchronní unzip s filtrem.
  const small = unzipSync(bytes, { filter: (f) => SMALL_FILES.test(f.name) });
  const findFile = (base: string): Uint8Array | undefined => {
    const key = Object.keys(small).find((k) => k === base || k.endsWith(`/${base}`));
    return key ? small[key] : undefined;
  };
  const stopsRaw = findFile('stops.txt');
  if (!stopsRaw) {
    throw new Error('GTFS archiv není úplný — chybí povinný soubor stops.txt.');
  }

  // 2) Zastávky v boxu → množina stop_id.
  const inBox = new Set<string>();
  {
    const text = strFromU8(stopsRaw);
    const lines = text.split(/\r?\n/);
    const header = splitCsvLine(lines[0]?.replace(/^﻿/, '') ?? '');
    const iId = header.indexOf('stop_id');
    const iLat = header.indexOf('stop_lat');
    const iLng = header.indexOf('stop_lon');
    if (iId < 0 || iLat < 0 || iLng < 0) {
      throw new Error('GTFS soubor stops.txt nemá očekávané sloupce (stop_id, stop_lat, stop_lon).');
    }
    const b = opts.bbox;
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i]) continue;
      const row = splitCsvLine(lines[i]);
      const lat = Number(row[iLat]);
      const lng = Number(row[iLng]);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      if (lat >= b.south && lat <= b.north && lng >= b.west && lng <= b.east) {
        inBox.add(row[iId]);
      }
    }
  }
  if (inBox.size === 0) {
    throw new Error('Ve zvolené oblasti (okolí vaší trasy) nejsou v tomto feedu žádné zastávky.');
  }

  // 3) Streamovaný průchod stop_times.txt — necháme jen řádky zastávek v boxu.
  const keptLines: string[] = [];
  let header = '';
  let stopIdIdx = -1;
  await streamZipEntry(bytes, /(^|\/)stop_times\.txt$/, (line, isFirst) => {
    if (isFirst) {
      header = line.replace(/^﻿/, '');
      stopIdIdx = splitCsvLine(header).indexOf('stop_id');
      return;
    }
    if (stopIdIdx < 0 || !line) return;
    // Rychlá cesta bez uvozovek; s uvozovkami plný rozklad.
    const cols = line.includes('"') ? splitCsvLine(line) : line.split(',');
    if (inBox.has(cols[stopIdIdx])) keptLines.push(line);
  });
  if (!header) {
    throw new Error('GTFS archiv není úplný — chybí povinný soubor stop_times.txt.');
  }

  // 4) Mini-zip (store, bez komprese — rychlé) → existující parser.
  const filtered: Record<string, Uint8Array> = {};
  for (const [name, data] of Object.entries(small)) {
    const base = name.split('/').pop()!;
    filtered[base] = data;
  }
  filtered['stop_times.txt'] = strToU8(header + '\n' + keptLines.join('\n') + '\n');
  const miniZip = zipSync(filtered, { level: 0 });

  // Po ořezu na oblast je linek přirozeně méně — vyšší strop, ať nevypadnou
  // příměstské (méně frekventované) linky a model netvrdí „tady MHD není".
  const result = parseGtfsZip(miniZip, { serviceDate: opts.serviceDate, maxLines: opts.maxLines ?? 500 });
  result.warnings.unshift(
    `Síť ořezána na okolí vaší trasy (${inBox.size} zastávek v oblasti) — linky mimo oblast se neimportují.`,
  );
  return result;
}

/**
 * Streamuje jeden soubor ze zipu po řádcích (TextDecoder se stream=true),
 * zip se krmí po 1MB kusech s průběžným uvolňováním event loopu.
 */
async function streamZipEntry(
  bytes: Uint8Array,
  namePattern: RegExp,
  onLine: (line: string, isFirst: boolean) => void,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const unzip = new Unzip();
    unzip.register(UnzipInflate);
    let found = false;
    let done = false;

    unzip.onfile = (file) => {
      if (!namePattern.test(file.name) || found) return;
      found = true;
      const decoder = new TextDecoder();
      let carry = '';
      let first = true;
      file.ondata = (err, data, final) => {
        if (err) {
          done = true;
          reject(new Error('GTFS soubor se nepodařilo rozbalit (poškozený zip?).'));
          return;
        }
        carry += decoder.decode(data, { stream: !final });
        let nl = carry.indexOf('\n');
        while (nl >= 0) {
          const line = carry.slice(0, nl).replace(/\r$/, '');
          carry = carry.slice(nl + 1);
          onLine(line, first);
          first = false;
          nl = carry.indexOf('\n');
        }
        if (final) {
          if (carry.trim()) onLine(carry.replace(/\r$/, ''), first);
          done = true;
          resolve();
        }
      };
      file.start();
    };

    // Krmení po kusech, mezi kusy pustíme event loop (UI nezamrzá).
    const CHUNK = 1 << 20;
    let offset = 0;
    const pump = (): void => {
      if (done) return;
      const end = Math.min(bytes.length, offset + CHUNK);
      const final = end >= bytes.length;
      try {
        unzip.push(bytes.subarray(offset, end), final);
      } catch {
        if (!done) {
          done = true;
          reject(new Error('GTFS zip se nepodařilo přečíst.'));
        }
        return;
      }
      offset = end;
      if (final) {
        // Soubor nebyl v zipu vůbec.
        if (!found && !done) {
          done = true;
          resolve();
        }
        return;
      }
      setTimeout(pump, 0);
    };
    pump();
  });
}

/** Lehký CSV rozklad jednoho řádku (uvozovky, zdvojené uvozovky). */
export function splitCsvLine(line: string): string[] {
  if (!line.includes('"')) return line.split(',');
  const out: string[] = [];
  let cur = '';
  let q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (q) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else q = false;
      } else cur += ch;
    } else if (ch === '"') q = true;
    else if (ch === ',') {
      out.push(cur);
      cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out;
}
